import React, { useState, useEffect, useCallback, memo, useMemo, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Autocomplete,
  Divider,
  Button,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Grid,
  Tooltip,
  Checkbox,
  ListSubheader
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSamples, deleteSample, invalidateSampleCache, getSamplesByDocumentId, recalculateAnalysemonsterDepths } from '../Lookup_components/Sample_lookup.jsx';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { supabase } from '../../supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import ClearIcon from '@mui/icons-material/Clear';
import Draggable from 'react-draggable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { AuthContext } from '../../context/AuthContext';
import { USER_ROLES } from '../../config/constants';
import { DOCUMENT_STATUSES } from '../../config/constants';

// Draggable Dialog Component
function DraggableDialog(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title">
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

// Reusable Autocomplete component
const AutocompleteField = memo(({ label, value, onChange, options, loading, keyPrefix, isReadOnly }) => {
  const [open, setOpen] = useState(false);

  const currentOption = options?.find(option => option.domain_value_id === value) || null;

  // Sort options alphabetically, except for unit fields
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Don't sort if the label contains "eenheid" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    if (isUnitField) {
      return options;
    }
    
    return [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
  }, [options, label]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={sortedOptions}
      getOptionLabel={(option) => option.description || ''}
      value={currentOption}
      onChange={(event, newValue) => {
        onChange(newValue?.domain_value_id || null);
      }}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal"
        />
      )}
      loading={loading}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      isOptionEqualToValue={(option, value) => option?.domain_value_id === value?.domain_value_id}
      renderOption={(props, option) => (
        <li {...props} key={`${keyPrefix}-${option.domain_value_id}-${option.description}`}>
          {option.description}
        </li>
      )}
    />
  );
});

export default function AnalysemonsterTab({ document, isReadOnly }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedChildItem, setExpandedChildItem] = useState(null);
  const [error, setError] = useState(null);
  const [boreholes, setBoreholes] = useState([]);
  const [childSamples, setChildSamples] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingVeldmonster, setIsCreatingVeldmonster] = useState(false);
  const [isSubmittingVeldmonster, setIsSubmittingVeldmonster] = useState(false);
  const [newVeldmonsterData, setNewVeldmonsterData] = useState(null);
  const [newMengmonster, setNewMengmonster] = useState({
    name: '',
    document_id: document.id,
    starttime: null,
    endtime: null,
    materialclass: null,
    upperdepth: null,
    upperdepth_uom: null,
    upperdepth_condition: null,
    lowerdepth: null,
    lowerdepth_uom: null,
    lowerdepth_condition: null,
    borehole_id: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedMengmonsterId, setSelectedMengmonsterId] = useState(null);

  // --- State for Linking Veldmonsters ---
  const [isLinkVeldmonsterDialogOpen, setIsLinkVeldmonsterDialogOpen] = useState(false);
  const [allVeldmonsters, setAllVeldmonsters] = useState([]);
  const [selectedVeldmonstersToLink, setSelectedVeldmonstersToLink] = useState([]);
  const [isLinkingVeldmonsters, setIsLinkingVeldmonsters] = useState(false);
  const [loadingDialogVeldmonsters, setLoadingDialogVeldmonsters] = useState(false);
  // --- End State ---

  // --- Get role from AuthContext --- 
  const { role } = useContext(AuthContext);
  const isAdmin = role === USER_ROLES.ADMIN; // Check if user is admin

  // Memoize the parameters for the useSamples hook to fetch MENGmonsters
  const analysemonsterParams = useMemo(() => ({
    document_id: document.id,
    type: 'analyse' // Gebruik 'analyse'
  }), [document.id]);

  // Fetch only mengmonsters (root samples) using useSamples hook
  const {
    samples: fetchedAnalysemonsters,
    loading: analysemonstersLoading,
    error: analysemonstersError, // Capture error
    refetch: refetchAnalysemonsters
  } = useSamples(analysemonsterParams); // Use useSamples with type

  // --- State for editable mengmonsters ---
  const [editableAnalysemonsters, setEditableAnalysemonsters] = useState([]);

  // Fetch specific units for length (similar to DrillPointsTab)
  const {
    domainValues: lengthUnitsData,
    loading: lengthUnitsLoading,
  } = useDomainValues({ technical_name: 'Eenheid', unit_dimension: 'l' });

  // Add new hook call for ReferentieVlak
  const {
    domainValues: referentieVlakData,
    loading: referentieVlakLoading,
  } = useDomainValues({ technical_name: 'Hoedanigheid', group_name: 'ReferentieVlak', sortByFrequency: true });

  // Get domain values - ensure all required are fetched
  const technicalNames = React.useMemo(() => ['Eenheid', 'Hoedanigheid', 'MateriaalKlasse', 'Compartiment'], []);
  const { domainValues, loading: domainValuesLoading } = useDomainValues(technicalNames);

  // Fetch boreholes
  useEffect(() => {
    const fetchBoreholes = async () => {
      try {
        const { data: projectData } = await supabase
          .from('document_project')
          .select('id')
          .eq('document_id', document.id)
          .single();

        if (projectData) {
          const { data } = await supabase
            .from('document_borehole')
            .select('id, name')
            .eq('project_id', projectData.id);

          setBoreholes(data || []);
        }
      } catch (error) {
        console.error('Error fetching boreholes:', error);
      }
    };

    fetchBoreholes();
  }, [document.id]);

  // Fetch child samples for a specific parent
  const fetchChildSamples = useCallback(async (parentId, forceRefresh = false) => {
    if (!parentId || (childSamples[parentId] && !forceRefresh)) return;

    setLoadingChildren(true);
    setError(null);
    try {
      // Stap 1: Haal de veldmonster_ids op uit de koppeltabel
      const { data: links, error: linkError } = await supabase
        .from('analysemonster_veldmonster_link')
        .select('veldmonster_id')
        .eq('analysemonster_id', parentId);

      if (linkError) throw linkError;

      const veldmonsterIds = links.map(link => link.veldmonster_id);

      if (veldmonsterIds.length === 0) {
        setChildSamples(prev => ({
          ...prev,
          [parentId]: []
        }));
        setLoadingChildren(false);
        return;
      }

      // Stap 2: Haal de veldmonsters op basis van de verkregen IDs
      const { data, error } = await supabase
        .from('document_sample')
        .select(`
          *,
          borehole:document_borehole!document_sample_borehole_id_fkey(name),
          lowerdepth_condition:document_domainvalueslookup!document_sample_lowerdepth_condition_fkey(description),
          lowerdepth_uom:document_domainvalueslookup!document_sample_lowerdepth_uom_fkey(description),
          upperdepth_condition:document_domainvalueslookup!document_sample_upperdepth_condition_fkey(description),
          upperdepth_uom:document_domainvalueslookup!document_sample_upperdepth_uom_fkey(description),
          materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(description)
        `)
        .eq('document_id', document.id) // Nog steeds filteren op document_id voor extra zekerheid/scoping
        .in('id', veldmonsterIds) // Gebruik .in() om meerdere IDs te matchen
        .eq('type', 'veld'); // Zorg ervoor dat we alleen veldmonsters ophalen

      if (error) throw error;

      setChildSamples(prev => ({
        ...prev,
        [parentId]: data || []
      }));
    } catch (error) {
      console.error('Error fetching child samples:', error);
      setError(`Fout bij ophalen deelmonsters: ${error.message}`);
    } finally {
      setLoadingChildren(false);
    }
  }, [document.id, childSamples]);

  // Load child samples when the current mengmonster changes
  useEffect(() => {
    if (editableAnalysemonsters && editableAnalysemonsters.length > 0 && currentIndex >= 0 && currentIndex < editableAnalysemonsters.length) {
      const currentParentId = editableAnalysemonsters[currentIndex]?.id;
      if (currentParentId) {
         fetchChildSamples(currentParentId);
      }
    }
  }, [currentIndex, editableAnalysemonsters, fetchChildSamples]);

  // Navigation handlers
  const handleNextAnalysemonster = () => {
    if (editableAnalysemonsters && currentIndex < editableAnalysemonsters.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setExpandedChildItem(null);
    }
  };

  const handlePrevAnalysemonster = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setExpandedChildItem(null);
    }
  };

  // Find the domain_value_id for 'Bodem/Sediment'
  const bodemSedimentId = useMemo(() => {
    const compartimenten = domainValues?.['Compartiment'];
    if (!compartimenten) return null;
    return compartimenten.find(opt => (opt.description || '').toLowerCase() === 'bodem/sediment')?.domain_value_id || null;
  }, [domainValues]);

  // Find the domain_value_id for 'meter'
  const meterUnitId = useMemo(() => {
    const units = lengthUnitsData?.['Eenheid'];
    if (!units) return null;
    const meterOption = units.find(unit => unit.description?.toLowerCase() === 'meter');
    return meterOption?.domain_value_id || null;
  }, [lengthUnitsData]);

  // Find the domain_value_id for 't.o.v. maaiveld'
  const defaultConditionId = useMemo(() => {
    const conditions = referentieVlakData?.['Hoedanigheid'];
    if (!conditions) return null;
    return conditions.find(cond => cond.description?.toLowerCase() === 't.o.v. maaiveld')?.domain_value_id || null;
  }, [referentieVlakData]);

  // Handle creating a new mengmonster
  const handleOpenCreateDialog = () => {
    setNewMengmonster({
      name: '',
      document_id: document.id,
      starttime: null,
      endtime: null,
      materialclass: bodemSedimentId, // Standaard Bodem/Sediment
      upperdepth: null,
      upperdepth_uom: meterUnitId, // Standaard meter
      upperdepth_condition: defaultConditionId, // Standaard t.o.v. maaiveld
      lowerdepth: null,
      lowerdepth_uom: meterUnitId, // Standaard meter
      lowerdepth_condition: defaultConditionId, // Standaard t.o.v. maaiveld
      borehole_id: null,
      analysemonster: true
    });
    setIsCreating(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreating(false);
  };

  const handleCreateAnalysemonster = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const createData = {
        id: uuidv4(),
        document_id: document.id,
        name: newMengmonster.name || `Nieuw Analysemonster ${editableAnalysemonsters.length + 1}`,
        starttime: newMengmonster.starttime ? new Date(newMengmonster.starttime).toISOString() : null,
        endtime: newMengmonster.endtime ? new Date(newMengmonster.endtime).toISOString() : null,
        materialclass: newMengmonster.materialclass || bodemSedimentId, // Standaard Bodem/Sediment
        upperdepth: newMengmonster.upperdepth,
        upperdepth_uom: newMengmonster.upperdepth_uom || meterUnitId, // Standaard meter
        upperdepth_condition: newMengmonster.upperdepth_condition || defaultConditionId, // Standaard t.o.v. maaiveld
        lowerdepth: newMengmonster.lowerdepth,
        lowerdepth_uom: newMengmonster.lowerdepth_uom || meterUnitId, // Standaard meter
        lowerdepth_condition: newMengmonster.lowerdepth_condition || defaultConditionId, // Standaard t.o.v. maaiveld
        borehole_id: newMengmonster.borehole_id,
        sample_id: null,
        type: 'analyse'
      }; 

      const { data, error } = await supabase
        .from('document_sample')
        .insert([createData])
        .select()
        .single();

      if (error) throw error;

      const newIndex = editableAnalysemonsters.length;
      setEditableAnalysemonsters(prev => [...prev, data]);

      setIsCreating(false);
      setNewMengmonster({
        name: '', 
        document_id: document.id, 
        starttime: null, 
        endtime: null, 
        materialclass: null,
        upperdepth: null, 
        upperdepth_uom: null, 
        upperdepth_condition: null, 
        lowerdepth: null,
        lowerdepth_uom: null, 
        lowerdepth_condition: null, 
        borehole_id: null,
      });
      setCurrentIndex(newIndex);
      setExpandedChildItem(null);
      invalidateSampleCache(document.id);

    } catch (error) {
      console.error('Error creating analysemonster:', error);
      setError(`Fout bij aanmaken analysemonster: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAnalysemonster = async (idToDelete) => {
    if (!idToDelete) {
        setError("Kon analysemonster niet verwijderen: Geen ID opgegeven.");
        setIsDeleteDialogOpen(false);
        return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      await deleteSample(idToDelete);

      let nextIndex = currentIndex;
      const currentLength = editableAnalysemonsters.length;
      setEditableAnalysemonsters(prev => {
          const newState = prev.filter(m => m.id !== idToDelete);
          if (currentIndex >= newState.length && newState.length > 0) {
              nextIndex = newState.length - 1;
          } else if (currentIndex >= currentLength && currentLength === 1) {
              nextIndex = 0;
          }
          return newState;
      });

      setCurrentIndex(nextIndex);

      setIsDeleteDialogOpen(false);
      setSelectedMengmonsterId(null);
      setExpandedChildItem(null);
      invalidateSampleCache(document.id);

    } catch (error) {
      console.error('Error deleting analysemonster:', error);
      setError(`Fout bij verwijderen analysemonster: ${error.message}`);
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (id) => {
      if (id) {
          setSelectedMengmonsterId(id);
          setIsDeleteDialogOpen(true);
      } else {
          console.error("Attempted to open delete dialog without a valid ID.");
          setError("Kan verwijder-dialoog niet openen: ongeldig ID.");
      }
  };

  const debouncedSaveCallback = useCallback(
    debounce(async (id, updates) => {
      if (!id) {
        console.warn("Debounced save called without ID, skipping.");
        return;
      }
      try {
        setError(null);

        const { error: supabaseError } = await supabase
          .from('document_sample')
          .update(updates)
          .eq('id', id);

        if (supabaseError) {
           setError(`Fout bij opslaan analysemonster ${id.substring(0,4)}...: ${supabaseError.message}`);
           return;
        }

        // First invalidate the cache
        await invalidateSampleCache(document.id);
        
      } catch (error) {
        setError(`Algemene fout bij opslaan analysemonster ${id.substring(0,4)}...: ${error.message}`);
      }
    }, 500), // Reduced debounce time to 500ms for better responsiveness
    [document.id] // Removed refetchAnalysemonsters dependency
  );

  const handleAnalysemonsterChange = useCallback((id, field, value) => {
    setEditableAnalysemonsters(prevAnalysemonsters =>
      prevAnalysemonsters.map(m =>
        m.id === id ? { ...m, [field]: value } : m
      )
    );

    debouncedSaveCallback(id, { [field]: value });
  }, [debouncedSaveCallback]);

  // --- Handle Linking Existing Veldmonsters ---
  const handleOpenLinkVeldmonsterDialog = async () => {
    const currentAnalysemonster = editableAnalysemonsters[currentIndex];
    console.log("handleOpenLinkVeldmonsterDialog aangeroepen");
    console.log("document.id:", document.id);
    console.log("currentAnalysemonster:", currentAnalysemonster);
    
    // Reset de selectie
    setSelectedVeldmonstersToLink([]);
    
    // Open de dialog eerst om een betere UX te bieden
    setIsLinkVeldmonsterDialogOpen(true);
    console.log("Dialog status gezet naar open");
    
    // Dan start het laden van de data
    setLoadingDialogVeldmonsters(true); 
    try {
      // Gebruik de functie om de veldmonsters op te halen
      await fetchAllVeldmonstersForDocument();
    } catch (error) {
      console.error("Fout bij ophalen veldmonsters voor dialog:", error);
      setError(`Kon veldmonsters niet laden: ${error.message}`);
    } finally {
      setLoadingDialogVeldmonsters(false);
    }
  };

  const handleCloseLinkVeldmonsterDialog = () => {
    setIsLinkVeldmonsterDialogOpen(false);
  };

  const handleLinkSelectedVeldmonsters = async () => {
    const currentAnalysemonsterId = editableAnalysemonsters[currentIndex]?.id;
    if (!currentAnalysemonsterId || selectedVeldmonstersToLink.length === 0) {
      setError("Selecteer minstens één veldmonster om te koppelen.");
      return;
    }

    try {
      setIsLinkingVeldmonsters(true);
      setError(null);

      // EERST: Check welke koppelingen al bestaan om dubbele inserties te voorkomen
      const { data: existingLinks, error: fetchExistingError } = await supabase
        .from('analysemonster_veldmonster_link')
        .select('veldmonster_id')
        .eq('analysemonster_id', currentAnalysemonsterId)
        .in('veldmonster_id', selectedVeldmonstersToLink);

      if (fetchExistingError) {
        console.error("Error checking existing links:", fetchExistingError);
        throw new Error(`Fout bij controleren bestaande koppelingen: ${fetchExistingError.message}`);
      }

      const alreadyLinkedIds = existingLinks?.map(l => l.veldmonster_id) || [];
      const newLinksToCreate = selectedVeldmonstersToLink.filter(id => !alreadyLinkedIds.includes(id));

      // Geef feedback over al gekoppelde monsters
      if (alreadyLinkedIds.length > 0) {
        const alreadyLinkedNames = allVeldmonsters
          .filter(vm => alreadyLinkedIds.includes(vm.id))
          .map(vm => vm.name || 'Naamloos')
          .join(', ');
        
        if (newLinksToCreate.length === 0) {
          setError(`Alle geselecteerde monsters (${alreadyLinkedNames}) waren al gekoppeld aan dit analysemonster.`);
          return;
        } else {
          console.warn(`Monsters ${alreadyLinkedNames} waren al gekoppeld en worden overgeslagen.`);
        }
      }

      // Als er geen nieuwe koppelingen zijn, stop hier
      if (newLinksToCreate.length === 0) {
        setError("Geen nieuwe koppelingen om toe te voegen.");
        return;
      }

      // Maak alleen de nieuwe koppelingen aan
      const linksToInsert = newLinksToCreate.map(veldmonsterId => ({
        analysemonster_id: currentAnalysemonsterId,
        veldmonster_id: veldmonsterId
      }));

      const { error: insertError } = await supabase
        .from('analysemonster_veldmonster_link')
        .insert(linksToInsert);

      if (insertError) {
        console.error("Error inserting new links:", insertError);
        throw new Error(`Fout bij aanmaken koppelingen: ${insertError.message}`);
      }

      // --- Recalculate Depths ---
      await recalculateAnalysemonsterDepths([currentAnalysemonsterId]);
      
      // Fetch children again to show the updated list
      fetchChildSamples(currentAnalysemonsterId, true);

      // Invalidate cache and refetch analysemonsters to update UI with new depths
      invalidateSampleCache(document.id);
      refetchAnalysemonsters();

      // Update de dialog data om fresh koppelingen te tonen
      await fetchAllVeldmonstersForDocument();

      // Geef succesbericht met details
      const newlyLinkedNames = allVeldmonsters
        .filter(vm => newLinksToCreate.includes(vm.id))
        .map(vm => vm.name || 'Naamloos')
        .join(', ');

      if (alreadyLinkedIds.length > 0) {
        const skippedNames = allVeldmonsters
          .filter(vm => alreadyLinkedIds.includes(vm.id))
          .map(vm => vm.name || 'Naamloos')
          .join(', ');
        console.info(`Gekoppeld: ${newlyLinkedNames}. Overgeslagen (al gekoppeld): ${skippedNames}`);
      }

      handleCloseLinkVeldmonsterDialog(); // Close dialog on success

    } catch (error) {
      console.error('Error linking veldmonsters:', error);
      setError(`Fout bij koppelen veldmonsters: ${error.message}`);
    } finally {
      setIsLinkingVeldmonsters(false);
    }
  };

  const handleVeldmonsterSelectionChange = (event, veldmonsterId) => {
    if (event.target.checked) {
      setSelectedVeldmonstersToLink(prev => [...prev, veldmonsterId]);
    } else {
      setSelectedVeldmonstersToLink(prev => prev.filter(id => id !== veldmonsterId));
    }
  };

  // --- Handle UNLINKING Veldmonster --- (Previously Delete)
  // This function now only sets sample_id to null
  const handleUnlinkVeldmonster = async (idToUnlink) => {
    if (!idToUnlink) {
      setError("Kon veldmonster niet ontkoppelen: Geen ID opgegeven.");
      return;
    }
    const currentAnalysemonsterId = editableAnalysemonsters[currentIndex]?.id;
    if (!currentAnalysemonsterId) {
      setError("Kan niet ontkoppelen: Huidig analysemonster niet gevonden.");
      return;
    }

    try {
      // Verwijder de koppeling uit de analysemonster_veldmonster_link tabel
      const { error: deleteLinkError } = await supabase
        .from('analysemonster_veldmonster_link')
        .delete()
        .eq('analysemonster_id', currentAnalysemonsterId)
        .eq('veldmonster_id', idToUnlink);

      if (deleteLinkError) throw deleteLinkError;

      // --- Recalculate Depths ---
      await recalculateAnalysemonsterDepths([currentAnalysemonsterId]);
      
      // Fetch children again to show the updated list
      fetchChildSamples(currentAnalysemonsterId, true);

      // Invalidate cache and refetch analysemonsters to update UI with new depths
      invalidateSampleCache(document.id);
      refetchAnalysemonsters();

    } catch (error) {
      console.error('Error deleting veldmonster:', error);
      setError(`Fout bij ontkoppelen veldmonster: ${error.message}`);
    }
  };

  // --- Fetch ALL Veldmonsters for the document ---
  const fetchAllVeldmonstersForDocument = useCallback(async () => {
    console.log("fetchAllVeldmonstersForDocument aangeroepen");
    if (!document.id) {
      console.error("document.id ontbreekt, kan geen veldmonsters ophalen");
      return;
    }
    try {
      console.log("Supabase query starten voor veldmonsters...");
      const { data: veldmonstersData, error: veldmonstersError } = await supabase
        .from('document_sample')
        // Vraag hier alle velden op die je nodig hebt voor weergave + het oude sample_id ter referentie
        .select('id, name, sample_id, upperdepth, lowerdepth, borehole_id, borehole:document_borehole!document_sample_borehole_id_fkey(name)') 
        .eq('document_id', document.id)
        .eq('type', 'veld')
        .order('name', { ascending: true, nullsFirst: false });

      if (veldmonstersError) throw veldmonstersError;
      console.log("Veldmonsters data ontvangen:", veldmonstersData?.length);

      let allLinksData = [];
      if (veldmonstersData && veldmonstersData.length > 0) {
        const veldmonsterIds = veldmonstersData.map(vm => vm.id);
        console.log("Supabase query starten voor links...");
        const { data: fetchedLinks, error: linksError } = await supabase
          .from('analysemonster_veldmonster_link')
          .select('analysemonster_id, veldmonster_id')
          .in('veldmonster_id', veldmonsterIds);

        if (linksError) throw linksError;
        allLinksData = fetchedLinks || [];
        console.log("Links data ontvangen:", allLinksData?.length);
      }

      const combinedVeldmonsters = (veldmonstersData || []).map(vm => {
        const linkedAnalysemonsterIds = allLinksData
          .filter(link => link.veldmonster_id === vm.id)
          .map(link => link.analysemonster_id);
        return {
          ...vm,
          linked_analysemonster_ids: linkedAnalysemonsterIds
        };
      });
      
      console.log("Gecombineerde data voor dialog:", combinedVeldmonsters?.length, "veldmonsters");
      setAllVeldmonsters(combinedVeldmonsters);

    } catch (err) {
      console.error("Error fetching all veldmonsters and links for dialog:", err);
      setError(`Fout bij ophalen van alle veldmonsters voor dialoog: ${err.message}`);
      setAllVeldmonsters([]); // Clear on error
    }
  }, [document.id, supabase]); // supabase toegevoegd als dependency
  // --- End Fetch --- 

  // Sync fetched data to local editable state
  useEffect(() => {
    if (fetchedAnalysemonsters) {
      // --- ADDED: Asynchronous processing function ---
      const process_analysemonsters = async () => {
        // Normalize the foreign key fields to ensure only the ID is stored
        const mappedAnalysemonsters = fetchedAnalysemonsters.map(m => ({
          ...m,
          materialclass: m.materialclass?.domain_value_id ?? m.materialclass ?? null,
          // Keep unit objects for now, they might be needed for display before defaulting
          upperdepth_uom: m.upperdepth_uom?.domain_value_id ?? m.upperdepth_uom ?? null,
          upperdepth_condition: m.upperdepth_condition?.domain_value_id ?? m.upperdepth_condition ?? null,
          lowerdepth_uom: m.lowerdepth_uom?.domain_value_id ?? m.lowerdepth_uom ?? null,
          lowerdepth_condition: m.lowerdepth_condition?.domain_value_id ?? m.lowerdepth_condition ?? null,
        }));

        // --- SORT the mapped monsters by name ---
        mappedAnalysemonsters.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          // Use localeCompare for proper sorting of text and numbers
          return nameA.localeCompare(nameB, 'nl', { numeric: true });
        });
        // ----------------------------------------

        // --- NEW: Apply default units ---
        // const monstersWithDefaults = await setDefaultAnalysemonsterUnits(mappedAnalysemonsters); // REMOVED
        // --- END NEW ---

        // Only update if the normalized data is different from the current state
        if (JSON.stringify(mappedAnalysemonsters) !== JSON.stringify(editableAnalysemonsters)) {
          setEditableAnalysemonsters(mappedAnalysemonsters || []);
          // Adjust currentIndex if it's out of bounds after update
          const newLength = mappedAnalysemonsters?.length || 0;
          if (currentIndex >= newLength && newLength > 0) {
            setCurrentIndex(newLength - 1);
          } else if (newLength === 0) {
            setCurrentIndex(0); // Reset index if array becomes empty
          }
        }
      };
      // --- END ADDED ---
      process_analysemonsters(); // Call the async processing function
    }
  }, [fetchedAnalysemonsters]); // Added dependencies

  // Combined loading state
  const isPageLoading = analysemonstersLoading || domainValuesLoading || lengthUnitsLoading || referentieVlakLoading;
  const combinedError = analysemonstersError || error;

  const isDialogDataReady = !domainValuesLoading && !lengthUnitsLoading && !referentieVlakLoading && !!newMengmonster;

  // --- Define handleReprocessAnalyse ---
  const handleReprocessAnalyse = async () => {
    setError(null); // Clear previous errors
    console.log("Reprocess Analyse clicked for document:", document.id);

    if (!document || !document.id) {
      setError("Document ID is niet beschikbaar. Kan analyse niet herverwerken.");
      console.error("Reprocess Analyse: Document ID missing.");
      return;
    }

    try {
      // 1. Update document status to 'INTAKE'
      const { error: docUpdateError } = await supabase
        .from('documents') // Corrected table name
        .update({ status: DOCUMENT_STATUSES.INTAKE }) // Use constant for status
        .eq('id', document.id);

      if (docUpdateError) {
        console.error("Error updating document status:", docUpdateError);
        throw new Error(`Fout bij bijwerken documentstatus: ${docUpdateError.message}`);
      }
      console.log(`Document status updated to ${DOCUMENT_STATUSES.INTAKE} for document_id:`, document.id);

      // 2. Update related job(s) in 'document_jobs' table
      const { error: jobUpdateError } = await supabase
        .from('document_jobs') // Corrected table name
        .update({ 
          status: 'pending', 
          // BELANGRIJK: De kolom 'specific_prompt_id' moet bestaan in de 'document_jobs' tabel.
          // Als deze niet bestaat, zal dit deel van de query een fout veroorzaken.
          specific_prompt_id: '4 - mengmonster' 
        })
        .eq('document_id', document.id); // Assuming this is the foreign key column

      if (jobUpdateError) {
        console.error("Error updating job status:", jobUpdateError);
        // Overweeg specifiekere foutmelding als de kolom niet bestaat.
        if (jobUpdateError.message.includes('column') && jobUpdateError.message.includes('does not exist')) {
            throw new Error(`Fout bij bijwerken jobstatus: Kolom 'specific_prompt_id' bestaat mogelijk niet in 'document_jobs'. Details: ${jobUpdateError.message}`);
        } else {
            throw new Error(`Fout bij bijwerken jobstatus: ${jobUpdateError.message}`);
        }
      }
      console.log("Job status updated to 'pending' and potentially specific_prompt_id for jobs related to document_id:", document.id);

      alert("Analyse herverwerking gestart: documentstatus naar 'INTAKE' en jobstatus naar 'pending'.");

    } catch (err) {
      console.error("Error in handleReprocessAnalyse:", err);
      setError(err.message || "Onbekende fout bij herverwerken analyse.");
    }
  };
  // --- End Define ---

  if (isPageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render de inhoud op basis van of er analysemonsters zijn of niet
  let content;
  if (editableAnalysemonsters.length === 0) {
    content = (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ mb: 2 }}>Geen analysemonsters gevonden</Typography>
        {/* Button "Nieuw Analysemonster" is now moved to the top section */}
        {/* Button "Reprocess Analyse" is now moved to the top section */}
      </Paper>
    );
  } else {
    const currentAnalysemonster = editableAnalysemonsters[currentIndex];
    if (!currentAnalysemonster) {
      content = (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    } else {
      const childSamplesForCurrent = currentAnalysemonster?.id ? (childSamples[currentAnalysemonster.id] || []) : [];

      content = (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<NavigateBeforeIcon />} 
              onClick={handlePrevAnalysemonster}
              disabled={currentIndex === 0}
            >
              Vorige
            </Button>
            
            <Box sx={{ width: '50%', mx: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="mengmonster-select-label">Selecteer Analysemonster</InputLabel>
                <Select
                  id="mengmonster-select"
                  labelId="mengmonster-select-label"
                  value={currentIndex}
                  onChange={(e) => {
                    setCurrentIndex(e.target.value);
                    setExpandedChildItem(null);
                  }}
                  label="Selecteer Analysemonster"
                  renderValue={(selectedIndex) => editableAnalysemonsters[selectedIndex]?.name || `Analysemonster ${selectedIndex + 1}`}
                >
                  {editableAnalysemonsters.map((sample, index) => (
                    <MenuItem key={sample.id} value={index}>
                      {sample.name || `Analysemonster ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Button 
              variant="outlined" 
              endIcon={<NavigateNextIcon />} 
              onClick={handleNextAnalysemonster}
              disabled={currentIndex === editableAnalysemonsters.length - 1}
            >
              Volgende
            </Button>
          </Box>

          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => openDeleteDialog(currentAnalysemonster.id)}
            disabled={isReadOnly}
              >
                Verwijderen
              </Button>
            </Box>
            
            <TextField
              label="Naam"
              value={currentAnalysemonster.name || ''}
              onChange={(e) => handleAnalysemonsterChange(currentAnalysemonster.id, 'name', e.target.value)}
              fullWidth
              margin="normal"
          disabled={isReadOnly}
            />

            <DatePicker
              label="Startdatum"
              value={currentAnalysemonster.starttime ? new Date(currentAnalysemonster.starttime) : null}
              onChange={(date) => handleAnalysemonsterChange(currentAnalysemonster.id, 'starttime', date?.toISOString())}
              format="dd-MM-yyyy"
          disabled={isReadOnly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                  InputProps: {
                    endAdornment: (
                      <>
                        {currentAnalysemonster.starttime && (
                          <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                            <IconButton
                              aria-label="clear date"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnalysemonsterChange(currentAnalysemonster.id, 'starttime', null);
                              }}
                              edge="end"
                              size="small"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }
                }
              }}
            />

            <AutocompleteField
              label="Veld matrix"
              value={currentAnalysemonster.materialclass}
              onChange={(newValue) => handleAnalysemonsterChange(currentAnalysemonster.id, 'materialclass', newValue)}
              options={domainValues['Compartiment'] || []}
              loading={domainValuesLoading}
              keyPrefix={`meng-matrix-${currentAnalysemonster.id}`}
          isReadOnly={isReadOnly}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Bovendiepte"
                type="number"
                value={currentAnalysemonster.upperdepth ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                  if (!isNaN(val) || val === null) {
                    handleAnalysemonsterChange(currentAnalysemonster.id, 'upperdepth', val);
                  }
                }}
                fullWidth
                margin="normal"
                sx={{ flex: 1 }}
          disabled={isReadOnly}
              />
              <Box sx={{ flex: 1 }}>
                <AutocompleteField
                  label="Eenheid"
                  value={currentAnalysemonster.upperdepth_uom}
                  onChange={(newValue) => handleAnalysemonsterChange(currentAnalysemonster.id, 'upperdepth_uom', newValue)}
                  options={lengthUnitsData?.['Eenheid'] || []}
                  loading={lengthUnitsLoading}
                  keyPrefix={`meng-upperdepth-uom-${currentAnalysemonster.id}`}
              isReadOnly={isReadOnly}
                />
              </Box>
            </Box>
            <AutocompleteField
              label="Bovendiepte Conditie"
              value={currentAnalysemonster.upperdepth_condition}
              onChange={(newValue) => handleAnalysemonsterChange(currentAnalysemonster.id, 'upperdepth_condition', newValue)}
              options={referentieVlakData?.['Hoedanigheid'] || []}
              loading={referentieVlakLoading}
              keyPrefix={`meng-upperdepth-cond-${currentAnalysemonster.id}`}
          isReadOnly={isReadOnly}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Onderdiepte"
                type="number"
                value={currentAnalysemonster.lowerdepth ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                  if (!isNaN(val) || val === null) {
                     handleAnalysemonsterChange(currentAnalysemonster.id, 'lowerdepth', val);
                  }
                }}
                fullWidth
                margin="normal"
                sx={{ flex: 1 }}
          disabled={isReadOnly}
              />
              <Box sx={{ flex: 1 }}>
                <AutocompleteField
                  label="Eenheid"
                  value={currentAnalysemonster.lowerdepth_uom}
                  onChange={(newValue) => handleAnalysemonsterChange(currentAnalysemonster.id, 'lowerdepth_uom', newValue)}
                  options={lengthUnitsData?.['Eenheid'] || []}
                  loading={lengthUnitsLoading}
                  keyPrefix={`meng-lowerdepth-uom-${currentAnalysemonster.id}`}
              isReadOnly={isReadOnly}
                />
              </Box>
            </Box>
            <AutocompleteField
              label="Onderdiepte conditie"
              value={currentAnalysemonster.lowerdepth_condition}
              onChange={(newValue) => handleAnalysemonsterChange(currentAnalysemonster.id, 'lowerdepth_condition', newValue)}
              options={referentieVlakData?.['Hoedanigheid'] || []}
              loading={referentieVlakLoading}
              keyPrefix={`meng-lowerdepth-cond-${currentAnalysemonster.id}`}
          isReadOnly={isReadOnly}
            />
          </Paper>

          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Gerelateerde Veldmonsters
              </Typography>
              <Tooltip title="Voeg een nieuw veldmonster toe en koppel aan dit analysemonster">
            <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenLinkVeldmonsterDialog}
                    disabled={!currentAnalysemonster?.id || isReadOnly}
                  >
                    Veldmonsters Koppelen
                  </Button>
            </span>
              </Tooltip>
            </Box>
            
            {loadingChildren && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {!loadingChildren && childSamplesForCurrent.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Geen veldmonsters gekoppeld aan dit analysemonster
              </Typography>
            )}
            
            {!loadingChildren && childSamplesForCurrent.length > 0 && (
              <List>
                {childSamplesForCurrent.map((childSample) => (
                  <React.Fragment key={childSample.id}>
                    <ListItem
                      sx={{ bgcolor: 'background.paper', display: 'flex', justifyContent: 'space-between' }}
                    >
                      <ListItemText
                        primary={`${childSample.name || 'Naamloos veldmonster'} - ${childSample.borehole?.name || 'Geen Boring'}`}
                        onClick={() => setExpandedChildItem(
                          expandedChildItem === childSample.id ? null : childSample.id
                        )}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Ontkoppel veldmonster van dit analysemonster">
                          <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnlinkVeldmonster(childSample.id);
                                  }}
                              disabled={isReadOnly}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={expandedChildItem === childSample.id ? "Details inklappen" : "Details uitklappen"}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedChildItem(
                                expandedChildItem === childSample.id ? null : childSample.id
                              );
                            }}
                          >
                            {expandedChildItem === childSample.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    <Collapse in={expandedChildItem === childSample.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, pl: 4, bgcolor: 'background.default', borderLeft: '2px solid grey.700', borderBottom: '1px solid grey.800' }}>
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Naam:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2" color="text.primary">{childSample.name || 'Niet opgegeven'}</Typography></Grid>
                        </Grid>
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Startdatum:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{childSample.starttime ? new Date(childSample.starttime).toLocaleDateString('nl-NL') : 'Niet opgegeven'}</Typography></Grid>
                        </Grid>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Veld matrix:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{childSample.materialclass?.description || 'Niet opgegeven'}</Typography></Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>Boventraject</Typography>
                        <Grid container spacing={1} sx={{ pl: 1 }}>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Diepte/Eenheid:</Typography></Grid>
                          <Grid item xs={8}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                               <Typography variant="body2">{childSample.upperdepth ?? 'N.v.t.'}</Typography>
                               <Typography variant="body2">{childSample.upperdepth_uom?.description || ''}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Conditie:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{childSample.upperdepth_condition?.description || 'N.v.t.'}</Typography></Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Ondertraject</Typography>
                        <Grid container spacing={1} sx={{ pl: 1 }}>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Diepte/Eenheid:</Typography></Grid>
                          <Grid item xs={8}>
                             <Box sx={{ display: 'flex', gap: 2 }}>
                                 <Typography variant="body2">{childSample.lowerdepth ?? 'N.v.t.'}</Typography>
                                 <Typography variant="body2">{childSample.lowerdepth_uom?.description || ''}</Typography>
                             </Box>
                          </Grid>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Conditie:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{childSample.lowerdepth_condition?.description || 'N.v.t.'}</Typography></Grid>
                        </Grid>

                        <Divider sx={{ my: 1, mt: 2 }} />

                        <Grid container spacing={1} sx={{ mt: 1 }}>
                           <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Boring:</Typography></Grid>
                           <Grid item xs={8}><Typography variant="body2">{childSample.borehole?.name || 'Niet gekoppeld'}</Typography></Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </>
      );
    }
  }

  // Altijd de dialogs renderen, onafhankelijk van het aantal analysemonsters  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Analysemonsters</Typography>

      {/* Top action buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={isReadOnly}
        >
          Nieuw Analysemonster
        </Button>
        <Button
          variant="outlined"
          onClick={handleReprocessAnalyse}
          disabled={!isAdmin || isReadOnly} // Disabled if not admin or if readOnly
        >
          Reprocess Analyse
        </Button>
      </Stack>

      {combinedError && !isPageLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {combinedError}
        </Alert>
      )}

      {content}

      {/* Delete dialog: Altijd beschikbaar wanneer nodig */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <DialogTitle>Analysemonster verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u dit analysemonster wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isReadOnly}>
            Annuleren
          </Button>
          <Button 
            onClick={() => handleDeleteAnalysemonster(selectedMengmonsterId)}
            color="error"
            disabled={isDeleting || isReadOnly}
            startIcon={isDeleting ? <CircularProgress size={24} /> : null}
          >
            {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Veldmonster dialog: Altijd beschikbaar wanneer nodig */}
      <Dialog 
        open={isLinkVeldmonsterDialogOpen} 
        onClose={handleCloseLinkVeldmonsterDialog}
        maxWidth="sm"
        PaperComponent={DraggableDialog}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)', // Maak backdrop goed zichtbaar
            },
          },
        }}
        PaperProps={{
          sx: { 
            width: '600px',  // Iets breder dan 500px
            maxHeight: '90vh', // Iets hoger dan 80vh
            '& .MuiDialogContent-root': {
              minHeight: '400px' // Minimum hoogte voor de content
            },
            zIndex: 9999 // Maak zeker dat het bovenop alles komt
          }
        }}
      >
        <DialogTitle 
          style={{ cursor: 'move' }} 
          id="draggable-dialog-title"
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <DragIndicatorIcon />
          Selecteer Veldmonsters om te Koppelen
        </DialogTitle>
        <DialogContent>
          {loadingDialogVeldmonsters ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Veldmonsters laden...</Typography>
            </Box>
          ) : allVeldmonsters.length === 0 ? (
            <Typography>Geen veldmonsters gevonden voor dit document.</Typography>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {allVeldmonsters.map((vm) => {
                const currentAnalysemonsterId = editableAnalysemonsters[currentIndex]?.id;
                const isLinkedToCurrent = vm.linked_analysemonster_ids?.includes(currentAnalysemonsterId);
                
                // Check if linked to any *other* analysemonster
                const isLinkedToOthers = vm.linked_analysemonster_ids?.some(id => id !== currentAnalysemonsterId);
                
                // Disable checkbox if already linked to the current analysemonster
                const isDisabled = isLinkedToCurrent;

                return (
                  <ListItem 
                    key={vm.id} 
                    dense={false} 
                    button 
                    disabled={isDisabled} 
                    onClick={(e) => {
                      if (isDisabled) return;
                      handleVeldmonsterSelectionChange({ target: { checked: !selectedVeldmonstersToLink.includes(vm.id) } }, vm.id);
                    }}
                    sx={{
                      py: 1.5,
                      borderBottom: '1px solid #eee',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedVeldmonstersToLink.includes(vm.id)}
                      tabIndex={-1}
                      disableRipple
                      disabled={isDisabled}
                      onChange={(event) => handleVeldmonsterSelectionChange(event, vm.id)}
                      sx={{ mr: 1 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {vm.name || 'Naamloos'} 
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          {vm.borehole ? `(${vm.borehole.name})` : '(Geen boring)'}
                        </Typography>
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, color: 'text.primary' }}>
                        Diepte: {vm.upperdepth ?? 'N/A'} - {vm.lowerdepth ?? 'N/A'} m
                      </Typography>
                     
                      {isLinkedToCurrent && (
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.disabled', fontStyle: 'italic' }}>
                          Al gekoppeld aan dit analysemonster
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkVeldmonsterDialog} disabled={isReadOnly}>Annuleren</Button>
          <Button
            onClick={handleLinkSelectedVeldmonsters}
            variant="contained"
            disabled={isLinkingVeldmonsters || selectedVeldmonstersToLink.length === 0 || isReadOnly}
            startIcon={isLinkingVeldmonsters ? <CircularProgress size={20} /> : null}
          >
            {isLinkingVeldmonsters ? 'Bezig met koppelen...' : 'Geselecteerde Koppelen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog: Altijd beschikbaar wanneer nodig */}
      <Dialog 
        open={isCreating} 
        onClose={handleCloseCreateDialog} 
        maxWidth="sm"
        PaperComponent={DraggableDialog}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        PaperProps={{
          sx: { 
            width: '500px',
            maxHeight: '80vh'
          }
        }}
      >
        {isDialogDataReady ? (
          <>
            <DialogTitle 
              style={{ cursor: 'move' }} 
              id="draggable-dialog-title"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            >
              <DragIndicatorIcon />
              Nieuw Analysemonster
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Naam"
                value={newMengmonster.name}
                onChange={(e) => setNewMengmonster(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                margin="normal"
                autoFocus
              />
              <DatePicker
                label="Startdatum"
                value={newMengmonster.starttime ? new Date(newMengmonster.starttime) : null}
                onChange={(newValue) => {
                  const validDate = newValue instanceof Date && !isNaN(newValue);
                  setNewMengmonster(prev => ({ ...prev, starttime: validDate ? newValue.toISOString() : null }));
                }}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    InputProps: {
                      endAdornment: (
                        <>
                          {newMengmonster.starttime && (
                            <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                              <IconButton
                                aria-label="clear date"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewMengmonster(prev => ({ ...prev, starttime: null }));
                                }}
                                edge="end"
                                size="small"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }
                  }
                }}
              />
              <DatePicker
                label="Einddatum"
                value={newMengmonster.endtime ? new Date(newMengmonster.endtime) : null}
                onChange={(newValue) => {
                  const validDate = newValue instanceof Date && !isNaN(newValue);
                  setNewMengmonster(prev => ({ ...prev, endtime: validDate ? newValue.toISOString() : null }));
                }}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    InputProps: {
                      endAdornment: (
                        <>
                          {newMengmonster.endtime && (
                            <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                              <IconButton
                                aria-label="clear date"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewMengmonster(prev => ({ ...prev, endtime: null }));
                                }}
                                edge="end"
                                size="small"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }
                  }
                }}
              />
              <AutocompleteField
                label="Veld matrix"
                value={newMengmonster.materialclass}
                onChange={(newValue) => setNewMengmonster(prev => ({ ...prev, materialclass: newValue }))}
                options={domainValues['Compartiment'] || []}
                loading={domainValuesLoading}
                keyPrefix="new-meng-matrix"
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Bovendiepte"
                  type="number"
                  value={newMengmonster.upperdepth ?? ''}
                  onChange={(e) => setNewMengmonster(prev => ({ ...prev, upperdepth: e.target.value ? parseFloat(e.target.value) : null }))}
                  fullWidth margin="normal" sx={{ flex: 1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <AutocompleteField
                    label="Eenheid"
                    value={newMengmonster.upperdepth_uom}
                    onChange={(newValue) => setNewMengmonster(prev => ({ ...prev, upperdepth_uom: newValue }))}
                    options={lengthUnitsData?.['Eenheid'] || []}
                    loading={lengthUnitsLoading}
                    keyPrefix="new-meng-upper-uom"
                  />
                </Box>
              </Box>
              <AutocompleteField
                label="Bovendiepte Conditie"
                value={newMengmonster.upperdepth_condition}
                onChange={(newValue) => setNewMengmonster(prev => ({ ...prev, upperdepth_condition: newValue }))}
                options={referentieVlakData?.['Hoedanigheid'] || []}
                loading={referentieVlakLoading}
                keyPrefix="new-meng-upper-cond"
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Onderdiepte"
                  type="number"
                  value={newMengmonster.lowerdepth ?? ''}
                  onChange={(e) => setNewMengmonster(prev => ({ ...prev, lowerdepth: e.target.value ? parseFloat(e.target.value) : null }))}
                  fullWidth margin="normal" sx={{ flex: 1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <AutocompleteField
                    label="Eenheid"
                    value={newMengmonster.lowerdepth_uom}
                    onChange={(newValue) => setNewMengmonster(prev => ({ ...prev, lowerdepth_uom: newValue }))}
                    options={lengthUnitsData?.['Eenheid'] || []}
                    loading={lengthUnitsLoading}
                    keyPrefix="new-meng-lower-uom"
                  />
                </Box>
              </Box>
              <AutocompleteField
                label="Onderdiepte Conditie"
                value={newMengmonster.lowerdepth_condition}
                onChange={(newValue) => setNewMengmonster(prev => ({ ...prev, lowerdepth_condition: newValue }))}
                options={referentieVlakData?.['Hoedanigheid'] || []}
                loading={referentieVlakLoading}
                keyPrefix="new-meng-lower-cond"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCreateDialog}>Annuleren</Button>
              <Button 
                onClick={handleCreateAnalysemonster} 
                variant="contained" 
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
              >
                {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
            <Typography>Laden...</Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
